import React, { useCallback, useEffect, useRef, useState } from "react";
import { debounce, hasTrue } from "@/helper/helper";
import useInputPhoneMask from "@/hooks/tools/useInputPhoneMask";
import { useUserBitcoin } from "@/states/qr_types/bitcoin.state";
import { useConfig } from "@/states/common.state";
import { useHandleGenerateResult } from "@/hooks/useHandleGenerateResult";
import { useSignUpModal } from "@/states/ui.state";
import useGenerateHomeQr from "@/components/page-modules/home-page/hooks/useGenerateHomeQr";
import classes from "./BitcoinHomepage.module.scss";
import { scrollToTopWithInputFocus } from "@/utils";
import { useTranslations } from "next-intl";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";
import { useQrShowState } from "@/states/qr.state";
import TypeInput from "../_shared/TypeInput";
type Errors = {
  amount?: string[];
  receiver?: string[];
};
const cryptoCurrencies = [{
  value: "bitcoin",
  label: "BTC"
}, {
  value: "bitcoincash",
  label: "BCH"
}, {
  value: "ether",
  label: "ETH"
}, {
  value: "litecoin",
  label: "LTC"
}, {
  value: "dash",
  label: "DASH"
}];
const Bitcoin = () => {
  const {
    receiver,
    amount,
    crypto_currency,
    message,
    isDownload,
    setParams
  } = useUserBitcoin();
  const {
    handleGenerateResult
  } = useHandleGenerateResult();
  const {
    payload,
    config
  } = useConfig();
  const {
    dashboardQr
  } = useQrShowState();
  const isMobile = useMediaQuery({
    maxWidth: 1025
  });
  const [isFirst, setIsFirst] = useState<boolean>(true);
  const {
    mutateHomeQr,
    isSuccess
  } = useGenerateHomeQr();
  const signupModalState = useSignUpModal();
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const amountInputRef = useRef<HTMLInputElement>(null);
  const receiverInputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("QrTypes.bitcoin");
  const setDebouncedErrors = useCallback(debounce((newErrors: Errors) => setErrors(newErrors), 500), []);
  useEffect(() => {
    if (hasTrue(hasTrue(!isFirst, signupModalState.isTriedDownload), isMobile)) {
      setDebouncedErrors(dashboardQr?.["validation_errors"] ?? null);
    }
  }, [receiver, amount, payload]);
  useEffect(() => {
    if (signupModalState.isTriedDownload) {
      queueMicrotask(() => {
        setErrors({});
        setTimeout(() => {
          const newErrors = dashboardQr?.["validation_errors"] as Errors;
          setDebouncedErrors(newErrors ?? null);
          if (newErrors) {
            if (newErrors?.amount) {
              return scrollToTopWithInputFocus(amountInputRef);
            }
            if (newErrors?.receiver) {
              scrollToTopWithInputFocus(receiverInputRef);
            }
          }
        }, 200);
      });
    }
  }, [signupModalState.isTempTrigger]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === "amount") {
      setParams({
        [name]: value,
        isDownload: value !== ""
      });
    } else {
      setParams({
        [name]: value
      });
    }
  };
  useEffect(() => {
    signupModalState.setIsTriedDownload(false);
    handleGenerateResult(null);
  }, []);
  useEffect(() => {
    if (!isFirst) {
      mutateHomeQr({
        params: {
          crypto_currency,
          amount,
          receiver,
          message,
          config
        }
      });
    }
  }, [receiver, amount, crypto_currency, message, isFirst, isDownload]);
  const optimizedFn = useCallback(debounce(handleChange, 500), []);
  const {
    onChangeEvent
  } = useInputPhoneMask(optimizedFn, "");
  return <div className={classes.Wrapper} data-sentry-component="Bitcoin" data-sentry-source-file="Bitcoin.tsx">
      <div className={classes.Header}>
        <span className={classes.step}>1</span>
        <p className={classes.TopTitle}>{t("title")}</p>
      </div>
      <div className={classes.Title}>{t("info")}</div>
      <div className={classes.RadioBox}>
        <span className={classes.RadioBoxTitle}>{t("cryptocurrency")}:</span>
        <div className={classes.RadioBoxItemsBox}>
          {cryptoCurrencies.map(crypto => <TypeInput key={crypto.value} label={crypto.label} name="crypto_currency" type="radio" value={crypto.value} checked={crypto_currency === crypto.value} onChange={e => optimizedFn.call(null, e)} />)}
        </div>
      </div>
      <div className={classes.Flex}>
        <div className={classes.InputContainer}>
          <TypeInput label={t("enterAmount")} placeholder={t("amount")} name="amount" onChange={e => {
          onChangeEvent(e);
          setIsFirst(false);
        }} inputRef={amountInputRef} defaultValue={amount} errors={errors?.["amount"]} noMargin data-sentry-element="TypeInput" data-sentry-source-file="Bitcoin.tsx" />
        </div>
        <div className={classes.InputContainer}>
          <TypeInput label={t("receiver")} placeholder={t("walletAddress")} name="receiver" inputRef={receiverInputRef} onChange={e => {
          optimizedFn.call(null, e), setIsFirst(false);
        }} defaultValue={receiver} errors={errors?.["receiver"]} cls={classes.TxtInput} noMargin data-sentry-element="TypeInput" data-sentry-source-file="Bitcoin.tsx" />
        </div>
      </div>
      <div className={classNames(classes.InputContainer, classes.InputMessage)}>
        <TypeInput label={t("addYourMessage")} placeholder={t("message")} name="message" onChange={e => optimizedFn.call(null, e)} defaultValue={message} cls={classes.TxtInput} noMargin data-sentry-element="TypeInput" data-sentry-source-file="Bitcoin.tsx" />
      </div>
    </div>;
};
export default Bitcoin;